import { DEFAULT_CONFIG, PRESETS, applyPreset, deepMerge } from './config.js';
import { createStore } from './store.js';
import { renderControls } from './ui/controls.js';
import { renderManualPanel } from './ui/manualPanel.js';
import { createEventLog } from './ui/eventLog.js';
import { renderDetail } from './ui/detailPanel.js';
import { createInventoryChart } from './ui/charts/assetInventory.js';
import { createRequestsPerAssetChart } from './ui/charts/requestsPerAsset.js';
import { createCacheBoxesChart } from './ui/charts/cacheBoxes.js';
import { createAssetsOverTimeChart } from './ui/charts/assetsOverTime.js';
import { createDeltaVsFullChart } from './ui/charts/deltaVsFullTime.js';
import { createAssetGraph } from './ui/charts/assetGraph.js';
import { fromUrl, writeUrl, exportJson, importJson, listPresets, savePreset, loadPreset } from './persistence.js';

// -------- Config initialization (URL > default)
let cfg = fromUrl() || structuredClone(DEFAULT_CONFIG);
// Remember the last non-manual traffic model so the mode toggle can restore it.
let lastSimulatedModel = cfg.traffic.model !== 'manual' ? cfg.traffic.model : 'poisson';
applyModeAttr(cfg);
function applyModeAttr(c) {
  document.body.dataset.currentMode = c.traffic.model === 'manual' ? 'manual' : 'simulated';
  document.getElementById('mode-manual').classList.toggle('active', c.traffic.model === 'manual');
  document.getElementById('mode-manual').setAttribute('aria-selected', String(c.traffic.model === 'manual'));
  document.getElementById('mode-simulated').classList.toggle('active', c.traffic.model !== 'manual');
  document.getElementById('mode-simulated').setAttribute('aria-selected', String(c.traffic.model !== 'manual'));
}

// -------- Store (mostly for last event / selected event)
const store = createStore({ cfg, snapshot: null, selectedEventId: null, failureCount: 0 });

// -------- Worker
const worker = new Worker(new URL('./sim/worker.js', import.meta.url), { type: 'module' });
worker.onmessage = (e) => {
  const msg = e.data;
  if (msg.type === 'snapshot') {
    store.set((s) => ({ ...s, snapshot: msg.snapshot }));
  } else if (msg.type === 'events') {
    log.pushEvents(msg.events);
    // Update failure counter + toasts.
    let addedFails = 0;
    for (const ev of msg.events) if (ev.failure) { addedFails++; showToast(ev.failure, ev.url); }
    if (addedFails) {
      store.set((s) => ({ ...s, failureCount: s.failureCount + addedFails }));
      updateFailureCounter();
    }
  }
};

// Init sim in worker.
worker.postMessage({ type: 'init', cfg });
worker.postMessage({ type: 'setSpeed', speed: 10 });

// -------- UI wiring

const paramsPanelBody = document.getElementById('params-panel-body');
const manualPanelEl = document.getElementById('manual-panel');

function onConfigChange(nextCfg) {
  cfg = nextCfg;
  if (cfg.traffic.model !== 'manual') lastSimulatedModel = cfg.traffic.model;
  writeUrl(cfg);
  applyModeAttr(cfg);
  worker.postMessage({ type: 'reset', cfg });
  log.clear();
  store.set({ cfg, snapshot: null, selectedEventId: null, failureCount: 0 });
  updateFailureCounter();
  renderManualPanel(manualPanelEl, cfg, null, manualHandlers);
}

const manualHandlers = {
  onRequest: (userId, assetId) => worker.postMessage({ type: 'manualRequest', userId, assetId }),
  onModify: (assetId) => worker.postMessage({ type: 'manualModify', assetId }),
  onAdvance: (seconds) => worker.postMessage({ type: 'advanceTime', seconds }),
};

renderControls(paramsPanelBody, cfg, onConfigChange);
renderManualPanel(manualPanelEl, cfg, null, manualHandlers);

// Presets dropdown
const presetSel = document.getElementById('preset');
for (const [k, p] of Object.entries(PRESETS)) {
  const opt = document.createElement('option'); opt.value = k; opt.textContent = p.label; presetSel.appendChild(opt);
}
for (const name of listPresets()) {
  const opt = document.createElement('option'); opt.value = 'user:' + name; opt.textContent = 'User: ' + name; presetSel.appendChild(opt);
}
presetSel.addEventListener('change', () => {
  if (!presetSel.value) return;
  let next;
  if (presetSel.value.startsWith('user:')) next = loadPreset(presetSel.value.slice(5));
  else next = applyPreset(cfg, presetSel.value);
  if (next) {
    renderControls(paramsPanelBody, next, onConfigChange);
    onConfigChange(next);
  }
});

// Mode toggle
document.getElementById('mode-manual').addEventListener('click', () => {
  if (cfg.traffic.model === 'manual') return;
  const next = deepMerge(cfg, { traffic: { model: 'manual' } });
  renderControls(paramsPanelBody, next, onConfigChange);
  onConfigChange(next);
  // Auto-pause: manual mode doesn't drive time forward on its own.
  if (playing) { playing = false; btnPlay.textContent = '\u25B6'; worker.postMessage({ type: 'pause' }); }
});
document.getElementById('mode-simulated').addEventListener('click', () => {
  if (cfg.traffic.model !== 'manual') return;
  const next = deepMerge(cfg, { traffic: { model: lastSimulatedModel } });
  renderControls(paramsPanelBody, next, onConfigChange);
  onConfigChange(next);
});

// Playback controls
let playing = false;
const btnPlay = document.getElementById('btn-play');
btnPlay.addEventListener('click', () => {
  playing = !playing;
  btnPlay.textContent = playing ? '⏸' : '▶';
  worker.postMessage({ type: playing ? 'play' : 'pause' });
});
document.getElementById('btn-step').addEventListener('click', () => worker.postMessage({ type: 'step' }));
document.getElementById('btn-reset').addEventListener('click', () => {
  worker.postMessage({ type: 'reset', cfg });
  log.clear();
  store.set((s) => ({ ...s, snapshot: null, selectedEventId: null, failureCount: 0 }));
  updateFailureCounter();
});
document.getElementById('speed').addEventListener('change', (e) => worker.postMessage({ type: 'setSpeed', speed: parseInt(e.target.value, 10) }));
document.getElementById('seed').addEventListener('change', (e) => {
  cfg = { ...cfg, seed: parseInt(e.target.value, 10) || 1 };
  writeUrl(cfg);
  worker.postMessage({ type: 'reset', cfg });
  log.clear();
});
document.getElementById('btn-batch').addEventListener('click', () => {
  const ticksPerSec = 1000 / cfg.tickMs;
  worker.postMessage({ type: 'batch', ticks: Math.round(ticksPerSec * 3600) });
});
document.getElementById('btn-export').addEventListener('click', () => exportJson(cfg));
document.getElementById('btn-import').addEventListener('click', () => document.getElementById('import-file').click());
document.getElementById('import-file').addEventListener('change', async (e) => {
  const file = e.target.files?.[0]; if (!file) return;
  const data = await importJson(file);
  if (data?.cfg) {
    renderControls(paramsPanelBody, data.cfg, onConfigChange);
    onConfigChange(data.cfg);
  }
});

// Event log
const log = createEventLog(document.getElementById('event-log-body'), {
  onSelect: (ev) => {
    store.set((s) => ({ ...s, selectedEventId: ev.id }));
    renderDetail(document.getElementById('detail-body'), ev);
  }
});

// Charts
const inventory = createInventoryChart(document.querySelector('#chart-inventory svg'));
const requests = createRequestsPerAssetChart(document.querySelector('#chart-requests svg'));
const cacheBox = createCacheBoxesChart(document.querySelector('#chart-cache canvas'));
const assetsTime = createAssetsOverTimeChart(document.querySelector('#chart-assets-time svg'));
const deltaTime = createDeltaVsFullChart(document.querySelector('#chart-delta-full svg'));
const graph = createAssetGraph(document.getElementById('asset-graph').querySelector('.panel-body'),
  document.getElementById('graph-svg'), document.getElementById('graph-canvas'));

document.getElementById('graph-window').addEventListener('change', (e) => graph.setWindow(parseInt(e.target.value, 10)));

// Legend content
renderLegend();

// Subscribe to snapshots.
let lastSnap = null;
store.subscribe((state) => {
  const snap = state.snapshot;
  if (!snap) return;
  lastSnap = snap;
  inventory.update(snap);
  requests.update(snap);
  cacheBox.update(snap);
  assetsTime.update(snap);
  deltaTime.update(snap);
  graph.update(snap);
  renderManualPanel(manualPanelEl, cfg, snap, manualHandlers);
  document.getElementById('sim-clock').textContent = `t=${snap.t.toFixed(1)}s`;
  document.getElementById('event-log-stats').textContent = `${snap.stats.totalEvents} events`;
});

writeUrl(cfg);

// ---- Toast + failure counter

function updateFailureCounter() {
  const el = document.getElementById('failure-counter');
  const count = store.get().failureCount;
  document.getElementById('failure-count').textContent = String(count);
  el.classList.toggle('active', count > 0);
}
function showToast(type, url) {
  const stack = document.getElementById('toast-stack');
  const el = document.createElement('div');
  el.className = 'toast' + (type === 'unsupported-encoding' ? ' warn' : '');
  el.textContent = `${type}: ${url}`;
  stack.appendChild(el);
  setTimeout(() => { el.classList.add('leaving'); setTimeout(() => el.remove(), 220); }, 2200);
  // Cap concurrent toasts.
  while (stack.children.length > 5) stack.firstElementChild.remove();
}

function renderLegend() {
  const body = document.getElementById('legend-body');
  body.innerHTML = '';
  const rows = [
    ['Full asset', 'full'],
    ['Delta', 'delta'],
    ['304 Not Modified (revalidated)', 'reval'],
    ['Client cache hit', 'cache'],
    ['Dictionary (double border)', 'dict'],
    ['Failure', 'fail'],
  ];
  for (const [label, cls] of rows) {
    const row = document.createElement('div'); row.className = 'row';
    const sw = document.createElement('div'); sw.className = 'swatch ' + cls;
    sw.style.background =
      cls === 'full' ? 'var(--kind-full)' :
      cls === 'delta' ? 'linear-gradient(90deg, var(--kind-delta), var(--kind-full))' :
      cls === 'reval' ? 'var(--kind-reval)' :
      cls === 'cache' ? 'var(--kind-cache)' :
      cls === 'fail'  ? 'var(--kind-fail)' :
      'transparent';
    row.appendChild(sw);
    const t = document.createElement('div'); t.textContent = label; row.appendChild(t);
    body.appendChild(row);
  }
}
